import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import type { PurchaseOrder, PurchaseOrderLine, Supplier } from '../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fmtSize = (ml?: number): string => {
  if (!ml) return '—';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
};

const fmtStatus = (status: string): string =>
  status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fmtCurrency = (n: number, currency: string): string => `${currency}${n.toFixed(2)}`;

const poNumber = (id: string): string => id.slice(-8).toUpperCase();

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#1a1a1a',
    paddingTop: 44,
    paddingBottom: 64,
    paddingHorizontal: 44,
    lineHeight: 1.4,
  },

  // ── Header ──────────────────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  venueName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    color: '#111',
    letterSpacing: 0.2,
    marginBottom: 2,
  },
  venueSubtitle: {
    fontSize: 7.5,
    color: '#999',
    letterSpacing: 0.8,
  },
  docTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 22,
    color: '#111',
    letterSpacing: 2,
    textAlign: 'right',
  },

  // ── Rule lines ───────────────────────────────────────────────────────────────
  ruleBold: {
    borderBottomWidth: 2,
    borderBottomColor: '#111',
    borderBottomStyle: 'solid',
    marginBottom: 14,
  },
  ruleLight: {
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    borderBottomStyle: 'solid',
    marginBottom: 14,
  },

  // ── Meta row ─────────────────────────────────────────────────────────────────
  metaRow: {
    flexDirection: 'row',
    marginBottom: 14,
  },
  metaItem: {
    marginRight: 28,
    minWidth: 72,
  },
  metaLabel: {
    fontSize: 6.5,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    marginBottom: 3,
  },
  metaValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: '#111',
  },

  // ── Supplier / delivery columns ───────────────────────────────────────────────
  twoCol: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  col: {
    flex: 1,
    marginRight: 24,
  },
  colLast: {
    flex: 1,
  },
  sectionLabel: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6.5,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 6,
  },
  supplierName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: '#111',
    marginBottom: 3,
  },
  supplierLine: {
    fontSize: 8.5,
    color: '#555',
    marginBottom: 2,
  },

  // ── Notes ────────────────────────────────────────────────────────────────────
  notesBlock: {
    borderLeftWidth: 2,
    borderLeftColor: '#ccc',
    borderLeftStyle: 'solid',
    paddingLeft: 10,
    paddingTop: 6,
    paddingBottom: 6,
    marginBottom: 18,
  },
  notesText: {
    fontSize: 8.5,
    color: '#555',
    lineHeight: 1.5,
  },

  // ── Table ────────────────────────────────────────────────────────────────────
  table: {
    marginBottom: 0,
  },
  tableHead: {
    flexDirection: 'row',
    backgroundColor: '#111',
    paddingVertical: 7,
    paddingHorizontal: 8,
  },
  thCell: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6.5,
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    borderBottomStyle: 'solid',
  },
  tableRowAlt: {
    backgroundColor: '#f7f7f7',
  },
  tdText: {
    fontSize: 8.5,
    color: '#222',
  },
  tdMuted: {
    fontSize: 8.5,
    color: '#777',
  },
  tdMono: {
    fontFamily: 'Helvetica',
    fontSize: 8.5,
    color: '#222',
  },

  // Column widths (must add up to ~1 / use fixed where needed)
  cItem:     { flex: 4 },
  cSize:     { flex: 1.2, textAlign: 'right' },
  cLocation: { flex: 1.8 },
  cQty:      { flex: 0.7, textAlign: 'right' },
  cCost:     { flex: 1.4, textAlign: 'right' },
  cTotal:    { flex: 1.4, textAlign: 'right' },

  // ── Totals ────────────────────────────────────────────────────────────────────
  totalsArea: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 10,
    borderTopWidth: 2,
    borderTopColor: '#111',
    borderTopStyle: 'solid',
    marginBottom: 4,
  },
  totalItem: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginLeft: 32,
  },
  totalLabel: {
    fontSize: 6.5,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  totalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: '#111',
  },
  totalNote: {
    fontSize: 7,
    color: '#bbb',
    marginTop: 4,
    textAlign: 'right',
  },

  // ── Footer ────────────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#e8e8e8',
    borderTopStyle: 'solid',
    paddingTop: 7,
  },
  footerLeft: {
    fontSize: 6.5,
    color: '#bbb',
  },
  footerRight: {
    fontSize: 6.5,
    color: '#bbb',
    textAlign: 'right',
  },
});

// ── Document component ────────────────────────────────────────────────────────

export interface PODocumentProps {
  order: PurchaseOrder;
  lines: PurchaseOrderLine[];
  supplier: Supplier | null;
  venueName: string;
  currency: string;
}

export const PurchaseOrderDocument: React.FC<PODocumentProps> = ({
  order,
  lines,
  supplier,
  venueName,
  currency,
}) => {
  const totalQty = lines.reduce((sum, l) => sum + l.orderedQty, 0);
  const hasAnyCost = lines.some(l => l.unitPrice != null);
  const grandTotal = hasAnyCost
    ? lines.reduce((sum, l) => sum + l.orderedQty * (l.unitPrice ?? 0), 0)
    : null;

  const generatedAt = new Date().toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Document title={`Purchase Order ${poNumber(order._id)}`} author={venueName}>
      <Page size="A4" orientation="portrait" style={s.page} wrap>

        {/* ── Page header ────────────────────────────────────────── */}
        <View style={s.header}>
          <View>
            <Text style={s.venueName}>{venueName}</Text>
            <Text style={s.venueSubtitle}>Venue Operations</Text>
          </View>
          <Text style={s.docTitle}>PURCHASE ORDER</Text>
        </View>

        <View style={s.ruleBold} />

        {/* ── PO metadata ────────────────────────────────────────── */}
        <View style={s.metaRow}>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>PO Number</Text>
            <Text style={s.metaValue}>{poNumber(order._id)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Status</Text>
            <Text style={s.metaValue}>{fmtStatus(order.status)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Order Date</Text>
            <Text style={s.metaValue}>{fmtDate(order.orderedAt)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Expected Delivery</Text>
            <Text style={s.metaValue}>{fmtDate(order.expectedDeliveryDate)}</Text>
          </View>
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Venue</Text>
            <Text style={s.metaValue}>{venueName}</Text>
          </View>
        </View>

        <View style={s.ruleLight} />

        {/* ── Supplier / Delivery To ──────────────────────────────── */}
        <View style={s.twoCol}>
          <View style={s.col}>
            <Text style={s.sectionLabel}>Supplier</Text>
            {supplier ? (
              <>
                <Text style={s.supplierName}>{supplier.name}</Text>
                {supplier.contactName ? (
                  <Text style={s.supplierLine}>Attn: {supplier.contactName}</Text>
                ) : null}
                {supplier.contactEmail ? (
                  <Text style={s.supplierLine}>{supplier.contactEmail}</Text>
                ) : null}
                {supplier.contactPhone ? (
                  <Text style={s.supplierLine}>{supplier.contactPhone}</Text>
                ) : null}
                {supplier.country ? (
                  <Text style={s.supplierLine}>{supplier.country}</Text>
                ) : null}
              </>
            ) : (
              <Text style={s.supplierLine}>—</Text>
            )}
          </View>
          <View style={s.colLast}>
            <Text style={s.sectionLabel}>Delivery To</Text>
            <Text style={s.supplierName}>{venueName}</Text>
          </View>
        </View>

        {/* ── Notes ────────────────────────────────────────────────── */}
        {order.notes ? (
          <View style={s.notesBlock}>
            <Text style={s.sectionLabel}>Notes</Text>
            <Text style={s.notesText}>{order.notes}</Text>
          </View>
        ) : null}

        {/* ── Lines table ──────────────────────────────────────────── */}
        <View style={s.table}>
          {/* Table header — dark bar */}
          <View style={s.tableHead} fixed>
            <Text style={[s.thCell, s.cItem]}>Item</Text>
            <Text style={[s.thCell, s.cSize]}>Size</Text>
            <Text style={[s.thCell, s.cLocation]}>Location</Text>
            <Text style={[s.thCell, s.cQty]}>Qty</Text>
            <Text style={[s.thCell, s.cCost]}>Unit Cost</Text>
            <Text style={[s.thCell, s.cTotal]}>Total</Text>
          </View>

          {lines.length === 0 ? (
            <View style={s.tableRow}>
              <Text style={[s.tdMuted, { flex: 1, textAlign: 'center' }]}>
                No lines on this order.
              </Text>
            </View>
          ) : (
            lines.map((line, idx) => {
              const lineTotal =
                line.unitPrice != null ? line.orderedQty * line.unitPrice : null;
              return (
                <View
                  key={line._id}
                  style={[s.tableRow, idx % 2 === 1 ? s.tableRowAlt : {}]}
                  wrap={false}
                >
                  <Text style={[s.tdText, s.cItem]}>{line.displayName}</Text>
                  <Text style={[s.tdMuted, s.cSize]}>{fmtSize(line.bottleSize)}</Text>
                  <Text style={[s.tdMuted, s.cLocation]}>
                    {line.locationName || '—'}
                  </Text>
                  <Text style={[s.tdMono, s.cQty]}>{line.orderedQty}</Text>
                  <Text style={[s.tdMuted, s.cCost]}>
                    {line.unitPrice != null ? fmtCurrency(line.unitPrice, currency) : '—'}
                  </Text>
                  <Text style={[s.tdText, s.cTotal]}>
                    {lineTotal != null ? fmtCurrency(lineTotal, currency) : '—'}
                  </Text>
                </View>
              );
            })
          )}
        </View>

        {/* ── Totals ───────────────────────────────────────────────── */}
        <View style={s.totalsArea}>
          <View style={s.totalItem}>
            <Text style={s.totalLabel}>Total Qty</Text>
            <Text style={s.totalValue}>{totalQty}</Text>
          </View>
          {grandTotal != null ? (
            <View style={s.totalItem}>
              <Text style={s.totalLabel}>Order Total</Text>
              <Text style={s.totalValue}>{fmtCurrency(grandTotal, currency)}</Text>
            </View>
          ) : null}
        </View>
        {!hasAnyCost && lines.length > 0 ? (
          <Text style={s.totalNote}>
            Unit costs not set — order total unavailable.
          </Text>
        ) : null}

        {/* ── Footer (fixed — appears on every page) ───────────────── */}
        <View style={s.footer} fixed>
          <Text style={s.footerLeft}>
            This document is system-generated. · Nota Wine
          </Text>
          <Text
            style={s.footerRight}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `Page ${pageNumber} of ${totalPages}  ·  Generated ${generatedAt}`
            }
          />
        </View>

      </Page>
    </Document>
  );
};

// ── Download helper ───────────────────────────────────────────────────────────

export async function downloadPurchaseOrderPdf(
  order: PurchaseOrder,
  lines: PurchaseOrderLine[],
  supplier: Supplier | null,
  venueName: string,
  currency: string,
): Promise<void> {
  const doc = (
    <PurchaseOrderDocument
      order={order}
      lines={lines}
      supplier={supplier}
      venueName={venueName}
      currency={currency}
    />
  );
  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `PO-${poNumber(order._id)}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
